import React from 'react';
import { Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Menu } from 'lucide-react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';

export default function AppLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Prevent body scroll when sidebar is open on mobile
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
      setScrollPosition(window.pageYOffset);
    } else {
      document.body.style.overflow = '';
    }
  }, [isSidebarOpen]);

  // Handle scroll position and sidebar positioning
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth <= 991) {
        const navbar = document.querySelector('nav');
        const navbarHeight = navbar?.getBoundingClientRect().height || 0;
        const scrollTop = window.pageYOffset;
        const sidebar = sidebarRef.current;

        if (sidebar) {
          const sidebarTop = Math.max(navbarHeight, scrollTop > navbarHeight ? 0 : navbarHeight - scrollTop);
          sidebar.style.top = `${sidebarTop}px`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Mobile menu button - Positioned in the content area */}
      <div className="lg:hidden container mx-auto px-4 py-2 flex items-center sticky top-16 bg-gray-50 z-30">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors min-h-[44px] px-2"
          aria-label="Toggle menu"
        >
          <Menu className="h-6 w-6" />
          <span className="text-sm font-medium">Menu</span>
        </button>
      </div>

      <div className="container mx-auto py-4 lg:py-8 lg:flex gap-8 relative">
        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className={`
            fixed lg:relative left-0 z-40 w-64 bg-gray-50 lg:bg-transparent p-4 lg:p-0 overflow-y-auto
            transform transition-transform duration-300 ease-in-out lg:transform-none
            h-[calc(100vh-4rem)] lg:h-auto
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          style={{ top: '4rem' }}
        >
          <Sidebar onClose={() => setIsSidebarOpen(false)} />
        </div>

        {/* Main content */}
        <main className="flex-1 lg:ml-0 px-4 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}